﻿/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, ScrollView,
  ActivityIndicator, Alert, Modal, RefreshControl, StatusBar, Share,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  API_BASE, getH, getBizId, fmtCurrency, fmtDate,
  normalizeCampaign, getPlatformConfig, STATUS_COLORS, postToPlatforms,
} from '../../../services/campaignAPI';

const P = '#0e4e96';
const Y = '#fdb913';

export default function CampaignDetailsScreen({ navigation, route }) {
  const rawParam = route?.params?.campaign || {};
  const campaignId = route?.params?.campaignId || rawParam?.id;

  const [campaign, setCampaign]       = useState(rawParam?.name ? normalizeCampaign(rawParam) : null);
  const [loading, setLoading]         = useState(!rawParam?.name);
  const [refreshing, setRefreshing]   = useState(false);
  const [activeTab, setActiveTab]     = useState('Overview');
  const [analytics, setAnalytics]     = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [posting, setPosting]         = useState(false);
  const [showConfirm, setShowConfirm] = useState({ visible: false, action: '' });

  const TABS = ['Overview', 'Analytics', 'Platforms', 'Assets', 'Settings'];

  useEffect(() => { load(); }, [campaignId]);
  useEffect(() => { if (activeTab === 'Analytics' && !analytics) loadAnalytics(); }, [activeTab]);

  const load = useCallback(async () => {
    if (!campaignId) return;
    try {
      const h = await getH();
      const res = await fetch(`${API_BASE}/api/data/campaigns/${campaignId}`, { headers: h });
      if (res.ok) {
        const json = await res.json().catch(() => ({}));
        const data = json?.data || json;
        if (data?.id || data?.name) setCampaign(normalizeCampaign(data));
      }
    } catch { /* keep passed data */ }
    finally { setLoading(false); setRefreshing(false); }
  }, [campaignId]);

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const h = await getH();
      const res = await fetch(`${API_BASE}/api/data/campaign_analytics?campaign_id=${campaignId}`, { headers: h });
      const json = await res.json().catch(() => ({}));
      if (json?.data || json?.impressions) {
        setAnalytics(json.data || json);
      } else {
        // Build analytics from campaign fields
        setAnalytics({
          impressions:  campaign?.impressions || 0,
          clicks:       campaign?.clicks || 0,
          conversions:  campaign?.conversions || 0,
          reach:        parseInt(campaign?.reach) || 0,
          ctr:          campaign?.clicks && campaign?.impressions ? ((campaign.clicks / campaign.impressions) * 100).toFixed(2) : 0,
          cpc:          campaign?.clicks && campaign?.spent ? (campaign.spent / campaign.clicks).toFixed(2) : 0,
          spent:        campaign?.spent || 0,
          budget:       campaign?.budget || 0,
          engagement:   campaign?.engagement || 0,
        });
      }
    } catch { } finally { setAnalyticsLoading(false); }
  };

  const updateStatus = async (newStatus) => {
    try {
      const h = await getH();
      const res = await fetch(`${API_BASE}/api/data/campaigns/${campaignId}`, {
        method: 'PUT', headers: h, body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setCampaign(c => ({ ...c, status: newStatus }));
        Alert.alert('Updated', `Campaign ${newStatus.toLowerCase()}`);
      }
    } catch (e) { Alert.alert('Error', e.message); }
    setShowConfirm({ visible: false, action: '' });
  };

  const deleteCampaign = () => {
    Alert.alert('Delete Campaign', 'This action cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        try {
          const h = await getH();
          await fetch(`${API_BASE}/api/data/campaigns/${campaignId}`, { method: 'DELETE', headers: h });
          navigation.goBack();
        } catch (e) { Alert.alert('Error', e.message); }
      }},
    ]);
  };

  const postToAllPlatforms = async () => {
    if (!campaign?.platforms?.length) { Alert.alert('No Platforms', 'Add platforms first'); return; }
    setPosting(true);
    const content = `📢 ${campaign.name}\n\n${campaign.description}\n\n${campaign.objectives?.map(o => `• ${o}`).join('\n') || ''}`;
    const results = await postToPlatforms(campaign.platforms, content, campaignId);
    const ok = results.filter(r => r.ok).map(r => r.platform);
    const fail = results.filter(r => !r.ok).map(r => r.platform);
    setPosting(false);
    Alert.alert('Social Media Update',
      `✅ Posted to: ${ok.join(', ') || 'none'}\n${fail.length ? `❌ Failed: ${fail.join(', ')}` : ''}`,
    );
  };

  const shareCampaign = async () => {
    if (!campaign) return;
    await Share.share({ message: `${campaign.name}\n${campaign.description || ''}\n\nManaged via JobEng` });
  };

  if (loading) return (
    <SafeAreaView style={s.safe}>
      <LinearGradient colors={[P, '#1e7be2']} style={s.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}><Ionicons name="arrow-back" size={22} color="#fff" /></TouchableOpacity>
        <Text style={s.headerTitle}>Campaign</Text>
        <View style={{ width: 36 }} />
      </LinearGradient>
      <View style={s.loading}><ActivityIndicator size={36} color={P} /><Text style={s.loadingText}>Loading...</Text></View>
    </SafeAreaView>
  );

  if (!campaign) return (
    <SafeAreaView style={s.safe}>
      <LinearGradient colors={[P, '#1e7be2']} style={s.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}><Ionicons name="arrow-back" size={22} color="#fff" /></TouchableOpacity>
        <Text style={s.headerTitle}>Not Found</Text><View style={{ width: 36 }} />
      </LinearGradient>
      <View style={s.loading}><Text style={s.loadingText}>Campaign not found</Text></View>
    </SafeAreaView>
  );

  const si = STATUS_COLORS[campaign.status] || STATUS_COLORS.Draft;
  const budgetPct = campaign.budget > 0 ? Math.min(100, Math.round((campaign.spent / campaign.budget) * 100)) : 0;

  const renderOverview = () => (
    <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} colors={[P]} />}>
      {/* Hero card */}
      <View style={s.heroCard}>
        <View style={s.heroTop}>
          <View style={[s.statusBadge, { backgroundColor: si.bg }]}>
            <View style={[s.statusDot, { backgroundColor: si.dot }]} />
            <Text style={[s.statusText, { color: si.text }]}>{campaign.status}</Text>
          </View>
          <TouchableOpacity style={s.shareBtn} onPress={shareCampaign}>
            <MaterialCommunityIcons name="share-variant" size={18} color={P} />
          </TouchableOpacity>
        </View>
        <Text style={s.heroName}>{campaign.name}</Text>
        {campaign.description ? <Text style={s.heroDesc}>{campaign.description}</Text> : null}
        <View style={s.heroMeta}>
          <View style={s.heroMetaItem}>
            <MaterialCommunityIcons name="calendar-start" size={14} color="#64748b" />
            <Text style={s.heroMetaText}>{fmtDate(campaign.startDate)} – {fmtDate(campaign.endDate)}</Text>
          </View>
        </View>
      </View>

      {/* Budget */}
      <View style={s.section}>
        <Text style={s.sectionTitle}>Budget</Text>
        <View style={s.budgetGrid}>
          {[['Total', fmtCurrency(campaign.budget)], ['Spent', fmtCurrency(campaign.spent)], ['Remaining', fmtCurrency(campaign.budget - campaign.spent)], ['Used', `${budgetPct}%`]].map(([l, v]) => (
            <View key={l} style={s.budgetItem}><Text style={s.budgetItemVal}>{v}</Text><Text style={s.budgetItemLabel}>{l}</Text></View>
          ))}
        </View>
        <View style={s.progressTrack}>
          <View style={[s.progressFill, { width: `${budgetPct}%`, backgroundColor: budgetPct > 90 ? '#dc3545' : P }]} />
        </View>
      </View>

      {/* Objectives */}
      {campaign.objectives?.length > 0 && (
        <View style={s.section}>
          <Text style={s.sectionTitle}>Objectives</Text>
          {campaign.objectives.map((o, i) => (
            <View key={i} style={s.objectiveRow}>
              <View style={s.objectiveDot} />
              <Text style={s.objectiveText}>{o}</Text>
            </View>
          ))}
        </View>
      )}

      {/* Target Audience */}
      {campaign.targetAudience ? (
        <View style={s.section}>
          <Text style={s.sectionTitle}>Target Audience</Text>
          <Text style={s.sectionBody}>{campaign.targetAudience}</Text>
        </View>
      ) : null}

      {/* Stats */}
      <View style={s.section}>
        <Text style={s.sectionTitle}>Performance</Text>
        <View style={s.statsGrid}>
          {[
            { label: 'Reach', value: campaign.reach || '—', icon: 'account-multiple' },
            { label: 'Impressions', value: campaign.impressions ? campaign.impressions.toLocaleString() : '—', icon: 'eye-outline' },
            { label: 'Clicks', value: campaign.clicks || '—', icon: 'cursor-default-click' },
            { label: 'Conversions', value: campaign.conversions || '—', icon: 'check-circle-outline' },
            { label: 'Engagement', value: campaign.engagement ? `${campaign.engagement}%` : '—', icon: 'heart-outline' },
          ].map(({ label, value, icon }) => (
            <View key={label} style={s.statCard}>
              <MaterialCommunityIcons name={icon} size={22} color={P} />
              <Text style={s.statVal}>{value}</Text>
              <Text style={s.statLbl}>{label}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Social Auto-post */}
      <View style={s.section}>
        <View style={s.sectionRow}>
          <Text style={s.sectionTitle}>Auto-Post to Social</Text>
          <TouchableOpacity style={[s.postAllBtn, posting && { opacity: 0.5 }]} onPress={postToAllPlatforms} disabled={posting}>
            {posting ? <ActivityIndicator size={14} color="#fff" /> : <MaterialCommunityIcons name="send" size={14} color="#fff" />}
            <Text style={s.postAllBtnText}>{posting ? 'Posting...' : 'Post to All'}</Text>
          </TouchableOpacity>
        </View>
        <View style={s.platformsGrid}>
          {(campaign.platforms || []).map((p, i) => {
            const cfg = getPlatformConfig(p);
            return (
              <View key={i} style={[s.platformCard, { borderColor: cfg.color + '40' }]}>
                <MaterialCommunityIcons name={cfg.icon} size={22} color={cfg.color} />
                <Text style={[s.platformCardName, { color: cfg.color }]}>{p}</Text>
              </View>
            );
          })}
        </View>
      </View>
    </ScrollView>
  );

  const renderAnalytics = () => (
    <ScrollView>
      {analyticsLoading ? (
        <View style={s.loading}><ActivityIndicator size={36} color={P} /></View>
      ) : analytics ? (
        <>
          <View style={s.section}>
            <Text style={s.sectionTitle}>Key Metrics</Text>
            <View style={s.statsGrid}>
              {[
                { label: 'Impressions',   value: analytics.impressions?.toLocaleString() || '0' },
                { label: 'Clicks',        value: analytics.clicks?.toLocaleString()      || '0' },
                { label: 'CTR',           value: analytics.ctr ? `${analytics.ctr}%`     : '0%' },
                { label: 'Conversions',   value: analytics.conversions?.toLocaleString() || '0' },
                { label: 'Cost/Click',    value: analytics.cpc ? fmtCurrency(analytics.cpc) : '—' },
                { label: 'Budget Used',   value: `${analytics.budget > 0 ? Math.round((analytics.spent/analytics.budget)*100) : 0}%` },
              ].map(({ label, value }) => (
                <View key={label} style={s.statCard}>
                  <Text style={s.statVal}>{value}</Text>
                  <Text style={s.statLbl}>{label}</Text>
                </View>
              ))}
            </View>
          </View>
          <View style={s.section}>
            <TouchableOpacity style={s.fullAnalyticsBtn} onPress={() => navigation.navigate('CampaignAnalyticsDashboard', { campaign: campaign._raw || campaign._raw })}>
              <MaterialCommunityIcons name="chart-line" size={18} color="#fff" />
              <Text style={s.fullAnalyticsBtnText}>Open Full Analytics Dashboard</Text>
            </TouchableOpacity>
          </View>
        </>
      ) : <View style={s.loading}><Text style={s.loadingText}>No analytics data</Text></View>}
    </ScrollView>
  );

  const renderPlatforms = () => (
    <ScrollView>
      <View style={s.section}>
        <Text style={s.sectionTitle}>Active Platforms</Text>
        {(campaign.platforms || []).map((p, i) => {
          const cfg = getPlatformConfig(p);
          return (
            <View key={i} style={s.platformRow}>
              <View style={[s.platformIcon, { backgroundColor: cfg.color + '20' }]}>
                <MaterialCommunityIcons name={cfg.icon} size={24} color={cfg.color} />
              </View>
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={s.platformRowName}>{p}</Text>
                {cfg.maxChars && <Text style={s.platformRowSub}>Max {cfg.maxChars.toLocaleString()} characters</Text>}
              </View>
              <TouchableOpacity style={s.postBtn} onPress={async () => {
                setPosting(true);
                const res = await postToPlatforms([p], `📢 ${campaign.name}\n\n${campaign.description}`, campaignId);
                setPosting(false);
                Alert.alert(res[0].ok ? '✅ Posted' : '❌ Failed', `${p}: ${res[0].ok ? 'Post published' : res[0].error || 'Error'}`);
              }}>
                <MaterialCommunityIcons name="send" size={14} color="#fff" />
                <Text style={s.postBtnText}>Post</Text>
              </TouchableOpacity>
            </View>
          );
        })}
      </View>
      <View style={s.section}>
        <TouchableOpacity style={s.aiBtn} onPress={() => navigation.navigate('CampaignAIContentAssistantScreen', { campaign: campaign._raw })}>
          <MaterialCommunityIcons name="robot-outline" size={18} color="#fff" />
          <Text style={s.aiBtnText}>Generate AI Content for These Platforms</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );

  const renderSettings = () => (
    <ScrollView>
      {[
        { label: 'Edit Campaign',            icon: 'pencil-outline',        action: () => navigation.navigate('EditCampaign', { campaign: campaign._raw }) },
        { label: 'Analytics Dashboard',      icon: 'chart-bar',             action: () => navigation.navigate('CampaignAnalyticsDashboard', { campaign: campaign._raw }) },
        { label: 'Budget Settings',          icon: 'currency-gbp',          action: () => navigation.navigate('BudgetSettings', { campaign: campaign._raw }) },
        { label: 'Targeting Settings',       icon: 'target',                action: () => navigation.navigate('TargetingSettings', { campaign: campaign._raw }) },
        { label: 'Schedule Settings',        icon: 'calendar-clock',        action: () => navigation.navigate('ScheduleSettings', { campaign: campaign._raw }) },
        { label: 'AI Content Assistant',     icon: 'robot-outline',         action: () => navigation.navigate('CampaignAIContentAssistantScreen', { campaign: campaign._raw }) },
        { label: 'Duplicate Campaign',       icon: 'content-copy',          action: () => navigation.navigate('CreateCampaign', { duplicateFrom: campaign._raw }) },
        { label: 'Export Campaign Data',     icon: 'download-outline',      action: () => shareCampaign() },
      ].map(({ label, icon, action }) => (
        <TouchableOpacity key={label} style={s.settingRow} onPress={action}>
          <MaterialCommunityIcons name={icon} size={20} color={P} />
          <Text style={s.settingText}>{label}</Text>
          <Ionicons name="chevron-forward" size={18} color="#ccc" />
        </TouchableOpacity>
      ))}
      <View style={{ margin: 16, gap: 10 }}>
        {campaign.status !== 'Paused' && campaign.status !== 'paused' && (
          <TouchableOpacity style={s.dangerOutlineBtn} onPress={() => updateStatus('Paused')}>
            <MaterialCommunityIcons name="pause-circle-outline" size={18} color="#f59e0b" />
            <Text style={[s.dangerOutlineBtnText, { color: '#f59e0b' }]}>Pause Campaign</Text>
          </TouchableOpacity>
        )}
        {(campaign.status === 'Paused' || campaign.status === 'paused') && (
          <TouchableOpacity style={s.dangerOutlineBtn} onPress={() => updateStatus('Active')}>
            <MaterialCommunityIcons name="play-circle-outline" size={18} color="#10b981" />
            <Text style={[s.dangerOutlineBtnText, { color: '#10b981' }]}>Resume Campaign</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity style={[s.dangerOutlineBtn, { borderColor: '#fee2e2' }]} onPress={deleteCampaign}>
          <MaterialCommunityIcons name="delete-outline" size={18} color="#dc3545" />
          <Text style={[s.dangerOutlineBtnText, { color: '#dc3545' }]}>Delete Campaign</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );

  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={s.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={{ padding: 4 }}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
        </TouchableOpacity>
        <Text style={s.headerTitle} numberOfLines={1}>{campaign.name}</Text>
        <TouchableOpacity style={s.headerBtn} onPress={() => navigation.navigate('EditCampaign', { campaign: campaign._raw })}>
          <MaterialCommunityIcons name="pencil-outline" size={18} color="#fff" />
        </TouchableOpacity>
      </LinearGradient>

      {/* Tabs */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.tabBar} contentContainerStyle={{ paddingHorizontal: 8, gap: 4, alignItems: 'center' }}>
        {TABS.map(tab => (
          <TouchableOpacity key={tab} style={[s.tab, activeTab === tab && s.tabActive]} onPress={() => setActiveTab(tab)}>
            <Text style={[s.tabText, activeTab === tab && s.tabTextActive]}>{tab}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <View style={{ flex: 1, backgroundColor: '#F0F4F8' }}>
        {activeTab === 'Overview'   && renderOverview()}
        {activeTab === 'Analytics'  && renderAnalytics()}
        {activeTab === 'Platforms'  && renderPlatforms()}
        {activeTab === 'Assets'     && (
          <ScrollView><View style={s.section}>
            <TouchableOpacity style={s.aiBtn} onPress={() => navigation.navigate('CampaignAIContentAssistantScreen', { campaign: campaign._raw })}>
              <MaterialCommunityIcons name="robot-outline" size={18} color="#fff" />
              <Text style={s.aiBtnText}>Create AI Content Assets</Text>
            </TouchableOpacity>
          </View></ScrollView>
        )}
        {activeTab === 'Settings'   && renderSettings()}
      </View>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#F0F4F8' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 14, paddingVertical: 12 },
  headerTitle: { flex: 1, fontSize: 16, fontWeight: '800', color: '#fff', textAlign: 'center', marginHorizontal: 8 },
  headerBtn: { padding: 6, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8 },
  tabBar: { backgroundColor: '#fff', maxHeight: 44, borderBottomWidth: 1, borderBottomColor: '#e8ecf0' },
  tab: { paddingHorizontal: 14, paddingVertical: 10, borderRadius: 8 },
  tabActive: { backgroundColor: '#0e4e9615' },
  tabText: { fontSize: 12, color: '#64748b', fontWeight: '600' },
  tabTextActive: { color: '#0e4e96', fontWeight: '800' },
  loading: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 40 },
  loadingText: { color: '#64748b', fontSize: 14 },
  heroCard: { backgroundColor: '#fff', margin: 12, borderRadius: 16, padding: 18, shadowColor: '#000', shadowOpacity: 0.06, shadowRadius: 8, elevation: 3 },
  heroTop: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 },
  statusBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  statusDot: { width: 7, height: 7, borderRadius: 4 },
  statusText: { fontSize: 12, fontWeight: '700' },
  shareBtn: { padding: 6 },
  heroName: { fontSize: 20, fontWeight: '900', color: '#1e293b', marginBottom: 8 },
  heroDesc: { fontSize: 14, color: '#64748b', lineHeight: 22, marginBottom: 8 },
  heroMeta: { gap: 4 },
  heroMetaItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  heroMetaText: { fontSize: 12, color: '#64748b' },
  section: { backgroundColor: '#fff', margin: 12, marginTop: 0, borderRadius: 14, padding: 16, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 6, elevation: 2 },
  sectionRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  sectionTitle: { fontSize: 14, fontWeight: '800', color: '#1e293b', marginBottom: 12 },
  sectionBody: { fontSize: 14, color: '#475569', lineHeight: 22 },
  budgetGrid: { flexDirection: 'row', marginBottom: 10 },
  budgetItem: { flex: 1, alignItems: 'center' },
  budgetItemVal: { fontSize: 15, fontWeight: '900', color: '#0e4e96' },
  budgetItemLabel: { fontSize: 10, color: '#64748b', fontWeight: '600' },
  progressTrack: { height: 6, backgroundColor: '#e8ecf0', borderRadius: 3 },
  progressFill: { height: 6, borderRadius: 3 },
  objectiveRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, marginBottom: 8 },
  objectiveDot: { width: 7, height: 7, borderRadius: 4, backgroundColor: '#0e4e96', marginTop: 6 },
  objectiveText: { flex: 1, fontSize: 14, color: '#475569', lineHeight: 22 },
  statsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  statCard: { flex: 1, minWidth: '30%', backgroundColor: '#F8FAFC', borderRadius: 12, padding: 12, alignItems: 'center', gap: 4 },
  statVal: { fontSize: 18, fontWeight: '900', color: '#0e4e96' },
  statLbl: { fontSize: 10, color: '#64748b', fontWeight: '600', textAlign: 'center' },
  postAllBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: '#0e4e96', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 10 },
  postAllBtnText: { color: '#fff', fontSize: 12, fontWeight: '700' },
  platformsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  platformCard: { flexDirection: 'column', alignItems: 'center', gap: 4, padding: 10, borderRadius: 10, borderWidth: 1.5, minWidth: 72 },
  platformCardName: { fontSize: 10, fontWeight: '700', textAlign: 'center' },
  platformRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  platformIcon: { width: 44, height: 44, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  platformRowName: { fontSize: 14, fontWeight: '700', color: '#1e293b' },
  platformRowSub: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  postBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: '#0e4e96', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8 },
  postBtnText: { color: '#fff', fontSize: 11, fontWeight: '700' },
  fullAnalyticsBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: '#0e4e96', padding: 14, borderRadius: 12 },
  fullAnalyticsBtnText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  aiBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: '#8b5cf6', padding: 14, borderRadius: 12 },
  aiBtnText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  settingRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#f1f5f9', gap: 12 },
  settingText: { flex: 1, fontSize: 14, color: '#1e293b', fontWeight: '500' },
  dangerOutlineBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 12, borderRadius: 12, borderWidth: 1.5, borderColor: '#fde68a' },
  dangerOutlineBtnText: { fontWeight: '700', fontSize: 14 },
});